import { useCallback, useEffect, useRef } from 'react';
import type { IScreensLogging } from '../../../../../api/types';
import { useLogAnalyticsEvent } from '../../../../../hooks';

/**
 * MarketSmallBadgeList 의 DISPLAY_CAROUSEL 발화 hook.
 * - logged.current 로 dedup. resetDisplayCarousel 호출 시 counter bump 로 재실행.
 * - parent isVisible 가드 — inner FlatList 가 부모 viewport 밖에서도 viewable 을
 *   보고하므로 명령형 logDisplayCarousel 호출에서도 부모 가시성을 다시 가드한다.
 * - useLayoutEffect 로 발화 — 자식 cell 의 useEffect 기반 DISPLAY_STORE 보다
 *   먼저 emit 되도록 보장 (CAROUSEL → STORE 순서).
 */
export const useLogDisplayCarousel = (
  logging?: IScreensLogging,
  isVisible?: boolean,
  isAwaitingDataChange?: boolean,
) => {
  const logged = useRef(false);

  // bridge 직접 호출 대신 AnalyticsContext 자동 머지 wrapper 사용 — SCREEN 등 공용 필드 누락 방지.
  const logAnalyticsEvent = useLogAnalyticsEvent();
  // 명령형 logDisplayCarousel이 stale closure 없이 latest 값을 읽도록 ref 동기화.
  const loggingRef = useRef(logging);
  loggingRef.current = logging;
  const isAwaitingDataChangeRef = useRef(isAwaitingDataChange);
  isAwaitingDataChangeRef.current = isAwaitingDataChange;
  // 부모(AtomicRenderer) viewport 가시성. inner FlatList 의 onViewableItemsChanged 가
  // 부모 viewport 와 무관하게 mount 즉시 viewable 을 보고하므로, 명령형 호출에서도
  // 부모 가시성을 한 번 더 가드해 viewport 밖 발화를 차단한다.
  const isVisibleRef = useRef(isVisible);
  isVisibleRef.current = isVisible;
  const logAnalyticsEventRef = useRef(logAnalyticsEvent);
  logAnalyticsEventRef.current = logAnalyticsEvent;

  const resetDisplayCarousel = useCallback(() => {
    logged.current = false;
  }, []);

  /**
   * DISPLAY_CAROUSEL을 명령형으로 1회 발화. onViewableItemsChanged 콜백에서
   * DISPLAY_STORE 보다 먼저 호출하여 CAROUSEL → STORE 순서를 보장하기 위해 노출.
   * `logged.current`로 useEffect 경로와 자동 dedup된다.
   *
   * MarketSmallBadgeList 의 백엔드 component.logging 은 analytics 필드가 비어 있을
   * 수 있어 GoodsCarousel 처럼 analytics 존재 가드를 두지 않는다 — SCREEN 등 공용
   * 필드는 AnalyticsContext (useLogAnalyticsEvent) 가 자동 머지.
   */
  const logDisplayCarousel = useCallback(() => {
    if (logged.current) return;
    // isVisible 이 truthy(=true) 일 때만 발화. undefined/false 는 차단.
    // mount 직후 AtomicRenderer 가 아직 viewport 판정을 못 한 undefined 상태에서
    // inner viewable 보고가 와서 가드 우회되던 케이스를 막기 위함.
    if (!isVisibleRef.current) return;
    if (isAwaitingDataChangeRef.current) return;
    logged.current = true;
    const currentLogging = loggingRef.current;
    logAnalyticsEventRef.current({
      name: 'DISPLAY_CAROUSEL',
      analytics: currentLogging?.analytics,
      ably_only_log: currentLogging?.ably_only_log,
    });
  }, []);

  useEffect(() => {
    logged.current = false;
  }, [logging]);

  return { resetDisplayCarousel, logDisplayCarousel };
};
